import {
  Box,
  Card,
  Container,
  Group,
  Image,
  SimpleGrid,
  Stack,
  Text,
  rgba,
  useMantineTheme,
} from "@mantine/core";
import { IconArrowRight, IconCalculator } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import image1 from "../assets/images/1.jpg";
import image2 from "../assets/images/2.jpg";
import image3 from "../assets/images/3.jpg";
import image4 from "../assets/images/4.jpg";
import { featuredCountryCityGroups } from "../data/cityPages";
import { servicePages } from "../data/servicePages";
import { getToolsBySlugs } from "../data/toolsCatalog";
import { PageContent } from "../Model/PageContent.model";
import { getContentByTitle } from "../Services/content.service";
import {
  formatReviewCount,
  getFeaturedReviews,
  getReviewSummary,
} from "../Services/review.service";
import Seo from "../seo/Seo";
import {
  buildCanonicalUrl,
  createBreadcrumbSchema,
  createOrganizationSchema,
  createWebsiteSchema,
} from "../seo/utils";
import Feature from "./Feature";
import ReviewsPreviewSection from "./Reviews/ReviewsPreviewSection";
import { HugeTitle } from "./Shared/HugeTitle";
import SectionHeader from "./Shared/SectionHeader";

function Home() {
  const { title } = useParams();
  const [pageContent, setPageContent] = useState<PageContent>();
  const theme = useMantineTheme();

  async function loadContent(pageTitle: string) {
    try {
      const res = await getContentByTitle(pageTitle);
      setPageContent(res);
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    if (title) {
      loadContent(title);
      return;
    }

    setPageContent(undefined);
  }, [title]);

  return (
    <>
      <Seo
        title="Find a Personal Tutor for Academic Success | SubjectBuddy"
        description="SubjectBuddy connects UK students with personal tutors and mentors for ethical academic learning, study skills, and confidence-building support."
        path="/"
        keywords={[
          "personal tutor uk",
          "academic tutoring uk",
          "study skills uk",
          "learning support uk",
          "mentor uk",
        ]}
        schema={[
          createOrganizationSchema(),
          createWebsiteSchema(),
          createBreadcrumbSchema([{ name: "Home", path: "/" }]),
          {
            "@context": "https://schema.org",
            "@type": "Service",
            name: "Academic mentoring and tutoring",
            provider: {
              "@type": "Organization",
              name: process.env.REACT_APP_NAME || "SubjectBuddy",
            },
            areaServed: "United Kingdom",
            url: buildCanonicalUrl("/"),
          },
        ]}
      />
      <Box
        p="sm"
        style={{
          position: "fixed",
          top: 60,
          left: 0,
          width: "100vw",
          zIndex: 100,
          background: theme.colors.green[8],
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Group
          gap={8}
          align="center"
          style={{ width: "100%", justifyContent: "center" }}
        >
          <Text
            size="sm"
            fw={500}
            style={{
              color: "#fff",
              fontFamily: "Fira Sans, Segoe UI, Arial, sans-serif",
              letterSpacing: "-0.5px",
              textAlign: "center",
            }}
          >
            Have questions? Message us on WhatsApp to find a personal tutor for
            skills, confidence, and academic support.
          </Text>
        </Group>
      </Box>
      <Box hiddenFrom="md">
        <div style={{ height: 60 }} />
      </Box>
      <Container
        mt={{
          base: 50,
          md: 100,
        }}
        fluid
        px={0}
        mx={0}
      >
        <Stack gap={30}>
          <Feature pageContent={pageContent} />
          <Box py={20} px={{ base: "md", md: "xl" }}>
            <Container size="xl">
              <ReviewsPreviewSection
                id="reviews"
                reviews={getFeaturedReviews(3)}
                description="Inspired by the review structure in the reference pages, this section brings student trust signals, verified feedback, and real assignment outcomes into one clear view."
              />
            </Container>
          </Box>
          <OurService />
          <ServiceLinks />
          <ToolsLinks />
          <CountryCityLinks />
          <OurPromise />
        </Stack>
      </Container>
    </>
  );
}

function ToolsLinks() {
  const theme = useMantineTheme();
  const legacyTools = [
    {
      title: "Semester Grade Calculator",
      path: "/tools/semester-grade-calculator",
      description:
        "Calculate your final semester grade using quarter grades, final exam scores, and grade weights.",
      cta: "Calculate Semester Grade",
    },
    {
      title: "Cursive Generator",
      path: "/tools/cursive-generator",
      description:
        "Turn normal text into cursive-style writing and copy-paste cursive names, words, and sentences instantly.",
      cta: "Generate Cursive Text",
    },
    {
      title: "Racine Carrée Calculator",
      path: "/tools/racine-carree-calculator",
      description:
        "Calculate square roots instantly and learn how racine carrée works with simple examples.",
      cta: "Calculate Square Root",
    },
    {
      title: "AP Bio Score Calculator",
      path: "/tools/ap-bio-score-calculator",
      description:
        "Estimate your AP Biology score from 1 to 5 using your multiple-choice and free-response practice scores.",
      cta: "Calculate AP Bio Score",
    },
    {
      title: "APUSH Score Calculator",
      path: "/tools/apush-score-calculator",
      description:
        "Estimate your AP U.S. History score from 1 to 5 using your MCQ, SAQ, DBQ, and LEQ practice scores.",
      cta: "Calculate APUSH Score",
    },
  ];
  void legacyTools;
  const homeToolMetadata: Record<string, { description: string; cta: string }> = {
    "semester-grade-calculator": {
      description:
        "Calculate your final semester grade using assignments, quarter grades, final exam scores, and weighted components.",
      cta: "Calculate Semester Grade",
    },
    "cursive-generator": {
      description:
        "Turn normal text into cursive-style writing and copy-paste cursive names, words, and sentences instantly.",
      cta: "Generate Cursive Text",
    },
    "ap-bio-score-calculator": {
      description:
        "Estimate your AP Biology score from multiple-choice and free-response practice scores.",
      cta: "Estimate AP Bio Score",
    },
    "apush-score-calculator": {
      description:
        "Estimate your AP U.S. History score from MCQ, SAQ, DBQ, and LEQ practice scores.",
      cta: "Estimate APUSH Score",
    },
    "racine-carree-calculator": {
      description:
        "Calculate square roots instantly and learn racine carrée examples for students.",
      cta: "Calculate Square Root",
    },
  };
  const tools = getToolsBySlugs([
    "semester-grade-calculator",
    "cursive-generator",
    "ap-bio-score-calculator",
    "apush-score-calculator",
    "racine-carree-calculator",
  ]);

  return (
    <Box py={30}>
      <Container size="lg">
        <Stack gap="lg">
          <SectionHeader>
            <HugeTitle
              style={{
                fontSize: "2.2rem",
                textAlign: "center",
              }}
            >
              Free Student Tools
            </HugeTitle>
          </SectionHeader>
          <Text c="dimmed" ta="center" maw={760} mx="auto">
            Use our free academic tools to estimate scores, plan assignments,
            generate citations, and improve your study workflow.
          </Text>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            {tools.map((tool) => (
              <Card
                key={tool.url}
                component={Link}
                to={tool.url}
                withBorder
                radius="lg"
                shadow="sm"
                className="study-tool-card"
                style={{
                  background: theme.white,
                  color: "inherit",
                  textDecoration: "none",
                }}
              >
                <Group justify="space-between" align="flex-start" wrap="nowrap">
                  <Group gap="md" align="flex-start" wrap="nowrap">
                    <Box
                      className="study-tool-icon"
                      style={{
                        color: theme.colors.green[7],
                        background: theme.colors.green[0],
                      }}
                    >
                      <IconCalculator size={28} />
                    </Box>
                    <Stack gap={6}>
                      <Text size="xs" tt="uppercase" fw={800} c="green">
                        {tool.category}
                      </Text>
                      <Text fw={800} c={theme.colors.green[8]} size="lg">
                        {tool.title}
                      </Text>
                      <Text size="sm" c="dimmed" style={{ lineHeight: 1.7 }}>
                        {homeToolMetadata[tool.slug]?.description ||
                          tool.description}
                      </Text>
                      <Text size="sm" fw={700} c="blue">
                        {homeToolMetadata[tool.slug]?.cta || "Use Tool"}
                      </Text>
                    </Stack>
                  </Group>
                  <IconArrowRight size={22} color={theme.colors.blue[6]} />
                </Group>
              </Card>
            ))}
          </SimpleGrid>
        </Stack>
      </Container>
    </Box>
  );
}

function CountryCityLinks() {
  const theme = useMantineTheme();

  return (
    <Box py={30}>
      <Container size="lg">
        <Stack gap="lg">
          <SectionHeader>
            <HugeTitle
              style={{
                fontSize: "2.2rem",
                textAlign: "center",
              }}
            >
              Assignment Help By Country
            </HugeTitle>
          </SectionHeader>
          <Text c="dimmed" ta="center" maw={760} mx="auto">
            Explore SubjectBuddy support across top education destinations and
            high-demand student cities.
          </Text>
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
            {featuredCountryCityGroups.map((group) => (
              <Card
                key={group.countrySlug}
                withBorder
                radius="lg"
                shadow="sm"
                className="country-city-card"
                style={{ background: theme.white }}
              >
                <Stack gap="sm">
                  <Text size="xs" tt="uppercase" fw={700} c="green">
                    {group.countryCode}
                  </Text>
                  <Text fw={800} c={theme.colors.green[8]} size="lg">
                    {group.country}
                  </Text>
                  <Text
                    component={Link}
                    to={`/${group.countrySlug}`}
                    size="sm"
                    fw={700}
                    c="blue"
                    style={{ textDecoration: "none" }}
                  >
                    View country page
                  </Text>
                  <Group gap="xs">
                    {group.cities.map((city) => (
                      <Text
                        key={city.path}
                        component={Link}
                        to={city.path}
                        size="sm"
                        fw={600}
                        className="country-city-link"
                      >
                        {city.city}
                      </Text>
                    ))}
                  </Group>
                </Stack>
              </Card>
            ))}
          </SimpleGrid>
        </Stack>
      </Container>
    </Box>
  );
}

function OurPromise() {
  const theme = useMantineTheme();
  const promises = [
    {
      icon: "Learning",
      title: "100% Learning Support",
      description:
        "Personalized, step-by-step guidance for every topic, project, or assignment. We help you learn smarter, not harder.",
      color: theme.colors.green[6],
    },
    {
      icon: "Ethics",
      title: "Ethical Learning Support",
      description:
        "We focus on building your skills with original explanations and a commitment to academic integrity.",
      color: theme.colors.teal[5],
    },
    {
      icon: "Mentors",
      title: "Human-Led Tutoring",
      description:
        "All support is delivered by experienced mentors so you grow your own confidence and capabilities.",
      color: theme.colors.lime[6],
    },
    {
      icon: "Privacy",
      title: "Privacy & Confidentiality Assured",
      description:
        "Your data and learning journey are always private. We guarantee confidentiality and secure support.",
      color: theme.colors.green[8],
    },
    {
      icon: "Timing",
      title: "On-Time Help, Every Time",
      description:
        "We respect your deadlines and deliver support promptly, so you never miss out.",
      color: theme.colors.blue[6],
    },
  ];

  return (
    <Stack gap={0}>
      <SectionHeader>
        <HugeTitle
          style={{
            fontSize: "2.5rem",
            textAlign: "center",
            marginBottom: "1rem",
          }}
        >
          What We Promise
        </HugeTitle>
      </SectionHeader>
      <Container mt={40} size="md">
        <Stack gap={30}>
          {promises.map((promise) => (
            <Group
              key={promise.title}
              align="flex-start"
              gap={18}
              style={{
                background: theme.colors.gray[0],
                borderRadius: theme.radius.md,
                padding: "1.2rem 1.5rem",
                boxShadow: theme.shadows.lg,
              }}
            >
              <Text
                style={{
                  fontSize: 20,
                  color: promise.color,
                  flexShrink: 0,
                  minWidth: 72,
                }}
              >
                {promise.icon}
              </Text>
              <Stack gap={2}>
                <Text
                  fw={700}
                  size="lg"
                  style={{
                    color: promise.color,
                    fontFamily: theme.fontFamily,
                  }}
                >
                  {promise.title}
                </Text>
                <Text size="sm" c="dimmed">
                  {promise.description}
                </Text>
              </Stack>
            </Group>
          ))}
        </Stack>
      </Container>
    </Stack>
  );
}

function OurService() {
  const theme = useMantineTheme();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const services = [
    {
      image: image4,
      title: "1-on-1 Tutoring",
      label: "1:1",
      description:
        "Book focused tutoring sessions for difficult subjects, weekly study support, and concept-by-concept guidance from experienced academic mentors.",
      color: theme.colors.green[6],
    },
    {
      image: image1,
      title: "Assignment Help",
      label: "Task",
      description:
        "Get structured help with assignment briefs, question planning, explanation of concepts, and clearer academic work across university subjects.",
      color: theme.colors.teal[5],
    },
    {
      image: image2,
      title: "Dissertation Help",
      label: "Thesis",
      description:
        "Work through dissertation topics, chapter structure, literature review planning, research direction, and academic writing with step-by-step support.",
      color: theme.colors.lime[6],
    },
    {
      image: image3,
      title: "Coding Project Help",
      label: "Code",
      description:
        "Get help understanding project requirements, debugging issues, improving logic, and documenting coding work in programming and technical courses.",
      color: theme.colors.green[5],
    },
    {
      image: image1,
      title: "Research Paper Help",
      label: "Paper",
      description:
        "Receive support with research planning, source organisation, argument structure, referencing, and turning ideas into a stronger paper draft.",
      color: theme.colors.blue[6],
    },
    {
      image: image3,
      title: "Presentation Help",
      label: "Slides",
      description:
        "Build clearer presentations with better slide flow, stronger speaking points, and practical help preparing for class presentations or viva-style delivery.",
      color: theme.colors.cyan[6],
    },
    {
      image: image2,
      title: "Exam Subject Help",
      label: "Exam",
      description:
        "Prepare for tests with topic revision, past-paper guidance, formula and concept review, and targeted support in the subjects you find hardest.",
      color: theme.colors.yellow[7],
    },
  ];

  return (
    <Box py={50} style={{ position: "relative", overflow: "hidden" }}>
      <Box
        style={{
          position: "absolute",
          inset: 0,
          background: rgba(theme.colors.green[2], 0.3),
          zIndex: 0,
        }}
      />

      <Container size="xl" style={{ position: "relative", zIndex: 1 }}>
        <Stack align="center" mb={60}>
          <SectionHeader>
            <HugeTitle
              style={{
                fontSize: "2.5rem",
                textAlign: "center",
                marginBottom: "1rem",
              }}
            >
              Our Services
            </HugeTitle>
          </SectionHeader>
        </Stack>

        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
          {services.map((service, index) => (
            <Card
              p={0}
              key={service.title}
              style={{
                position: "relative",
                backgroundColor: "transparent",
                overflow: "hidden",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                transform:
                  hoveredIndex === index ? "translateY(-8px)" : "translateY(0)",
                cursor: "pointer",
                borderRadius: theme.radius.lg,
                height: "400px",
                display: "flex",
              }}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <Group wrap="nowrap" gap={0} align="stretch" style={{ flex: 1 }}>
                <Box
                  style={{
                    flex: "1 1 55%",
                    position: "relative",
                    zIndex: 2,
                    padding: theme.spacing.xl,
                    display: "flex",
                    flexDirection: "column",
                    background: theme.white,
                    height: "400px",
                  }}
                >
                  <Stack
                    justify="flex-start"
                    gap="md"
                    style={{ height: "100%" }}
                  >
                    <Box
                      style={{
                        width: 56,
                        height: 56,
                        borderRadius: "50%",
                        backgroundColor: rgba(service.color, 0.1),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "0.9rem",
                        fontWeight: 700,
                        transition: "transform 0.3s ease",
                        transform:
                          hoveredIndex === index ? "scale(1.1)" : "scale(1)",
                        flexShrink: 0,
                      }}
                    >
                      {service.label}
                    </Box>

                    <Text
                      size="xl"
                      fw={600}
                      style={{
                        color: service.color,
                        transition: "transform 0.3s ease",
                        transform:
                          hoveredIndex === index
                            ? "translateX(4px)"
                            : "translateX(0)",
                        flexShrink: 0,
                      }}
                    >
                      {service.title}
                    </Text>

                    <Text
                      size="sm"
                      style={{
                        color: theme.colors.gray[7],
                        lineHeight: 1.6,
                        flex: 1,
                        overflow: "auto",
                      }}
                    >
                      {service.description}
                    </Text>

                    <Box
                      style={{
                        width: hoveredIndex === index ? "90%" : "40%",
                        height: 3,
                        background: `linear-gradient(to right, ${service.color}, transparent)`,
                        transition: "all 0.3s ease",
                        borderRadius: theme.radius.xl,
                        flexShrink: 0,
                      }}
                    />
                  </Stack>
                </Box>

                <Box
                  style={{
                    flex: "1 1 45%",
                    position: "relative",
                    overflow: "hidden",
                    clipPath: "polygon(10% 0, 100% 0, 100% 100%, 0% 100%)",
                    height: "400px",
                  }}
                >
                  <Image
                    src={service.image}
                    alt={service.title}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      objectPosition: "center",
                      transition: "transform 0.5s ease",
                      transform:
                        hoveredIndex === index
                          ? "scale(1.1) translateX(-5%)"
                          : "scale(1.05) translateX(0)",
                    }}
                  />
                  <Box
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: `linear-gradient(to right,
                        ${rgba(theme.white, 0.95)} 0%,
                        ${rgba(theme.white, 0.8)} 20%,
                        ${rgba(service.color, 0.1)} 60%,
                        transparent 100%
                      )`,
                      opacity: hoveredIndex === index ? 0.8 : 1,
                      transition: "opacity 0.3s ease",
                    }}
                  />
                </Box>
              </Group>

              <Box
                style={{
                  position: "absolute",
                  top: 20,
                  right: "45%",
                  width: 120,
                  height: 120,
                  borderRadius: "50%",
                  background: `radial-gradient(circle at center,
                    ${rgba(service.color, 0.1)} 0%,
                    transparent 70%
                  )`,
                  opacity: hoveredIndex === index ? 1 : 0,
                  transform: hoveredIndex === index ? "scale(1)" : "scale(0.8)",
                  transition: "all 0.3s ease",
                  zIndex: 1,
                }}
              />
            </Card>
          ))}
        </SimpleGrid>
      </Container>
    </Box>
  );
}

function ServiceLinks() {
  const theme = useMantineTheme();
  const reviewSummary = getReviewSummary();

  return (
    <Box py={30}>
      <Container size="lg">
        <Stack gap="lg">
          <SectionHeader>
            <HugeTitle
              style={{
                fontSize: "2.2rem",
                textAlign: "center",
              }}
            >
              Explore Popular Support Areas
            </HugeTitle>
          </SectionHeader>
          <Text c="dimmed" ta="center" maw={720} mx="auto">
            Browse focused service pages for the academic areas students search
            for most often, then dive into related guides and resources.
          </Text>
          <SimpleGrid cols={{ base: 1, md: 2, lg: 3 }} spacing="lg">
            {servicePages.map((service) => (
              <Card
                key={service.slug}
                component={Link}
                to={`/services/${service.slug}`}
                withBorder
                radius="xl"
                shadow="sm"
                style={{
                  textDecoration: "none",
                  background: theme.white,
                }}
              >
                <Stack gap="sm">
                  <Text size="xs" tt="uppercase" fw={700} c="blue">
                    {service.cluster}
                  </Text>
                  <Text fw={700} c={theme.colors.green[8]}>
                    {service.heroTitle}
                  </Text>
                  <Text size="sm" fw={700} c="green">
                    {reviewSummary.averageRating.toFixed(1)}/5 from{" "}
                    {formatReviewCount(reviewSummary.totalReviews)} reviews
                  </Text>
                  <Text size="sm" c="dimmed" style={{ lineHeight: 1.7 }}>
                    {service.metaDescription}
                  </Text>
                  <Text size="sm" fw={600} c="blue">
                    View support page
                  </Text>
                </Stack>
              </Card>
            ))}
          </SimpleGrid>
          <Text ta="center" size="sm">
            <Text
              component={Link}
              to="/blogs"
              c="blue"
              fw={600}
              style={{ textDecoration: "none" }}
            >
              Read all student guides and blog articles
            </Text>
          </Text>
        </Stack>
      </Container>
    </Box>
  );
}

export default Home;
